/**
 * The console's first view — everything the payload supports without a click.
 */

import type { ValidationChain } from '../../../../types/dataQuality';
import AgentReviewPanel from '../AgentReviewPanel';
import AssuranceTiles from '../AssuranceTiles';
import DataSupplyChart from '../DataSupplyChart';
import PeriodProfileChart from '../PeriodProfileChart';
import ScoreCompositionBar from '../ScoreCompositionBar';
import TrendPill from '../TrendPill';
import YearStrip from '../YearStrip';
import { agentDelta } from '../dqSelectors';

export interface SummaryViewProps {
  chain: ValidationChain;
  onSelectYear?: (year: number) => void;
}

export default function SummaryView({ chain, onSelectYear }: SummaryViewProps) {
  const { summary } = chain;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <p className="max-w-prose text-sm leading-relaxed text-neutral-700">{summary.headline}</p>
        <TrendPill chain={chain} verbose />
      </div>

      <AssuranceTiles summary={summary} />

      {/* Both children return null on a rule-scored chain with no AI status,
          which left an empty grid row and its gaps behind. Only render the row
          when something will actually occupy it. */}
      {(agentDelta(summary) != null || summary.ai_status) && (
        <div className="grid gap-4 lg:grid-cols-2">
          <ScoreCompositionBar summary={summary} />
          <AgentReviewPanel summary={summary} />
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <DataSupplyChart statements={summary.statements} />
        <PeriodProfileChart periods={summary.periods} onSelectYear={onSelectYear} />
      </div>

      <YearStrip periods={summary.periods} onSelectYear={onSelectYear} />
    </div>
  );
}
